import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Download, Trash2, FileText, Mail, LayoutTemplate, Sparkles, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatError } from "../lib/api";
import { generateMedicalReportPDF } from "../lib/medicalReportPdf";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";

const emptyForm = {
  patient_id: "",
  appointment_id: "",
  report_date: new Date().toISOString().slice(0, 10),
  comment: "",
  reason: "",
  current_illness: "",
  diagnosis_impression: "",
  indications: "",
  send_email: true,
};

const emptyTemplate = {
  id: null,
  name: "",
  reason: "",
  current_illness: "",
  diagnosis_impression: "",
  indications: "",
};

function TemplatesManager({ open, onOpenChange, templates, currentUserId, onChange }) {
  const [editing, setEditing] = useState(emptyTemplate);
  const [saving, setSaving] = useState(false);

  const startNew = () => setEditing(emptyTemplate);
  const startEdit = (t) => setEditing({ ...t });

  const canEdit = (t) => !!t.owner_id && t.owner_id === currentUserId;

  const save = async (e) => {
    e.preventDefault();
    if (!editing.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        reason: editing.reason,
        current_illness: editing.current_illness,
        diagnosis_impression: editing.diagnosis_impression,
        indications: editing.indications,
      };
      if (editing.id) {
        await api.put(`/medical-reports/templates/${editing.id}`, payload);
        toast.success("Plantilla actualizada");
      } else {
        await api.post("/medical-reports/templates", payload);
        toast.success("Plantilla creada");
      }
      setEditing(emptyTemplate);
      onChange && onChange();
    } catch (err) { toast.error(formatError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar la plantilla "${t.name}"?`)) return;
    try {
      await api.delete(`/medical-reports/templates/${t.id}`);
      toast.success("Plantilla eliminada");
      onChange && onChange();
      if (editing.id === t.id) setEditing(emptyTemplate);
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="templates-manager-dialog">
        <DialogHeader>
          <DialogTitle>Plantillas de informe</DialogTitle>
          <DialogDescription>
            Plantillas reutilizables que rellenan Motivo, Enfermedad actual, Impresión y Indicaciones con un solo clic.
          </DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Existentes ({templates.length})</div>
              <Button size="sm" variant="outline" onClick={startNew} data-testid="tpl-new-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Nueva
              </Button>
            </div>
            <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
              {templates.length === 0 && <div className="text-xs text-muted-foreground">Sin plantillas.</div>}
              {templates.map((t) => (
                <div key={t.id} className={`flex items-center justify-between border border-border rounded-md px-3 py-2 ${editing.id === t.id ? "ring-2 ring-primary/40" : ""}`} data-testid={`tpl-row-${t.id}`}>
                  <button type="button" onClick={() => startEdit(t)} className="text-left min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{t.name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {t.owner_id ? "Personal" : "Global"} · {t.reason?.slice(0, 60) || "—"}
                    </div>
                  </button>
                  {canEdit(t) && (
                    <Button variant="ghost" size="icon" onClick={() => remove(t)} data-testid={`tpl-del-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <form onSubmit={save} className="space-y-2">
            <div className="text-sm font-medium">{editing.id ? "Editar plantilla" : "Nueva plantilla"}</div>
            {editing.id && !editing.owner_id && (
              <div className="text-[11px] rounded bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1">
                Plantilla global (solo lectura). Duplícala renombrándola para crear una versión personal.
              </div>
            )}
            <div>
              <Label className="text-xs">Nombre *</Label>
              <Input value={editing.name} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} placeholder="p. ej. Otitis media aguda" data-testid="tpl-name" />
            </div>
            <div>
              <Label className="text-xs">Motivo de consulta</Label>
              <Input value={editing.reason} onChange={(e) => setEditing((p) => ({ ...p, reason: e.target.value }))} data-testid="tpl-reason" />
            </div>
            <div>
              <Label className="text-xs">Enfermedad actual</Label>
              <Textarea rows={3} value={editing.current_illness} onChange={(e) => setEditing((p) => ({ ...p, current_illness: e.target.value }))} data-testid="tpl-current" />
            </div>
            <div>
              <Label className="text-xs">Impresión diagnóstica</Label>
              <Textarea rows={2} value={editing.diagnosis_impression} onChange={(e) => setEditing((p) => ({ ...p, diagnosis_impression: e.target.value }))} data-testid="tpl-diag" />
            </div>
            <div>
              <Label className="text-xs">Indicaciones</Label>
              <Textarea rows={4} value={editing.indications} onChange={(e) => setEditing((p) => ({ ...p, indications: e.target.value }))} data-testid="tpl-ind" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving || !editing.name.trim()} className="flex-1" data-testid="tpl-save-btn">
                {saving ? "Guardando..." : (editing.id ? "Guardar cambios" : "Crear plantilla")}
              </Button>
              {editing.id && (
                <Button type="button" variant="ghost" onClick={() => setEditing(emptyTemplate)}>Cancelar</Button>
              )}
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MedicalReports() {
  const { user } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplMgrOpen, setTplMgrOpen] = useState(false);

  const isDoctor = user?.role === "doctor" || user?.role === "admin";

  const load = () => api.get("/medical-reports").then(({ data }) => setItems(data));
  const loadTemplates = () =>
    api.get("/medical-reports/templates").then(({ data }) => setTemplates(data || [])).catch(() => {});

  useEffect(() => {
    load();
    if (isDoctor) {
      api.get("/patients")
        .then(({ data }) => setPatients(data.map((p) => ({ id: p.id, name: p.name }))))
        .catch(() => {});
      loadTemplates();
    }
    // If we arrive with a preselected patient/appointment (from Patients, Appointments or Teleconsulta), open the dialog
    const pid = location.state?.patientId;
    const aid = location.state?.appointmentId;
    const preReason = location.state?.reason;
    if (pid && isDoctor) {
      setForm((p) => ({
        ...p,
        patient_id: pid,
        appointment_id: aid || "",
        reason: preReason || p.reason,
      }));
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // Cleanup empty optional fields
      ["comment", "current_illness", "diagnosis_impression", "indications", "report_date"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) delete payload[k];
      });
      // Client controls email (with PDF attachment). Server-side send is disabled.
      const wantsEmail = !!payload.send_email;
      payload.send_email = false;
      const { data } = await api.post("/medical-reports", payload);
      setOpen(false);
      setForm(emptyForm);
      load();
      // Generate PDF locally (downloads) and, if the doctor asked for email, attach and re-send
      const built = await generateMedicalReportPDF(data);
      if (wantsEmail && data.patient_email && !String(data.patient_email).endsWith("@nolinked.local")) {
        try {
          await api.post(`/medical-reports/${data.id}/send-pdf`, { pdf_base64: built.base64 });
          toast.success("Informe creado y enviado por email con PDF adjunto");
        } catch (err) {
          toast.warning("Informe creado; el envío del PDF por email falló. Puedes reenviarlo desde la lista.");
        }
      } else {
        toast.success("Informe creado");
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este informe? Esta acción es irreversible.")) return;
    try {
      await api.delete(`/medical-reports/${id}`);
      toast.success("Informe eliminado");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const resend = async (rep) => {
    try {
      toast.info("Generando PDF y enviando…");
      const built = await generateMedicalReportPDF(rep, { download: false });
      await api.post(`/medical-reports/${rep.id}/send-pdf`, { pdf_base64: built.base64 });
      toast.success("Informe reenviado por email con PDF adjunto");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const applyTemplate = (tplId) => {
    if (!tplId) return;
    const t = templates.find((x) => x.id === tplId);
    if (!t) return;
    setForm((p) => ({
      ...p,
      reason: t.reason || p.reason,
      current_illness: t.current_illness || p.current_illness,
      diagnosis_impression: t.diagnosis_impression || p.diagnosis_impression,
      indications: t.indications || p.indications,
    }));
    toast.success(`Plantilla "${t.name}" aplicada`);
  };

  return (
    <div className="space-y-6 fade-up" data-testid="medical-reports-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-3xl flex items-center gap-3">
          <FileText className="w-8 h-8 text-primary" />
          Informes médicos de consulta
        </h1>
        {isDoctor && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setTplMgrOpen(true)} data-testid="manage-templates-btn">
              <LayoutTemplate className="w-4 h-4 mr-2" /> Plantillas
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="new-medical-report-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nuevo informe
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Emitir informe médico de consulta</DialogTitle>
                <DialogDescription>
                  Se generará un PDF con formato clínico oficial, firmado electrónicamente (HMAC-SHA256 + sello de tiempo) y verificable mediante código QR público.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                {templates.length > 0 && (
                  <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">Aplicar plantilla</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5" data-testid="mr-template-chips">
                      {templates.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => applyTemplate(t.id)}
                          className="text-xs px-2.5 py-1 rounded-full bg-white border border-border hover:bg-primary hover:text-white hover:border-primary transition-colors"
                          data-testid={`mr-tpl-chip-${t.id}`}
                        >
                          {t.name}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setTplMgrOpen(true)}
                        className="text-xs px-2.5 py-1 rounded-full text-primary hover:bg-primary/10 transition-colors flex items-center gap-1"
                        data-testid="mr-tpl-manage"
                      >
                        <Pencil className="w-3 h-3" /> Gestionar
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Paciente *</Label>
                    <Select value={form.patient_id} onValueChange={(v) => setForm((p) => ({ ...p, patient_id: v }))}>
                      <SelectTrigger data-testid="mr-patient"><SelectValue placeholder="Seleccione un paciente" /></SelectTrigger>
                      <SelectContent>
                        {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Fecha</Label>
                    <Input type="date" value={form.report_date} onChange={(e) => setForm((p) => ({ ...p, report_date: e.target.value }))} data-testid="mr-date" />
                  </div>
                </div>

                <div>
                  <Label>Comentario</Label>
                  <Input value={form.comment} onChange={(e) => setForm((p) => ({ ...p, comment: e.target.value }))} placeholder="Observaciones generales (opcional)" data-testid="mr-comment" />
                </div>

                <div>
                  <Label>Motivo de consulta *</Label>
                  <Textarea required rows={2} value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} placeholder="Por qué acude el paciente..." data-testid="mr-reason" />
                </div>

                <div>
                  <Label>Enfermedad actual</Label>
                  <Textarea rows={6} value={form.current_illness} onChange={(e) => setForm((p) => ({ ...p, current_illness: e.target.value }))} placeholder="Anamnesis: inicio, evolución, síntomas, factores asociados..." data-testid="mr-current-illness" />
                </div>

                <div>
                  <Label>Impresión diagnóstica</Label>
                  <Textarea rows={3} value={form.diagnosis_impression} onChange={(e) => setForm((p) => ({ ...p, diagnosis_impression: e.target.value }))} placeholder="Diagnóstico presuntivo / juicio clínico..." data-testid="mr-diagnosis" />
                </div>

                <div>
                  <Label>Indicaciones</Label>
                  <Textarea rows={5} value={form.indications} onChange={(e) => setForm((p) => ({ ...p, indications: e.target.value }))} placeholder="Plan terapéutico, tratamiento, controles, recomendaciones..." data-testid="mr-indications" />
                </div>

                <div className="flex items-center gap-2">
                  <input id="mr-send-email" type="checkbox" checked={form.send_email} onChange={(e) => setForm((p) => ({ ...p, send_email: e.target.checked }))} data-testid="mr-send-email" />
                  <Label htmlFor="mr-send-email" className="cursor-pointer">Enviar notificación por email al paciente</Label>
                </div>

                <Button type="submit" disabled={saving || !form.patient_id || !form.reason} className="w-full bg-primary hover:bg-primary/90" data-testid="mr-save-btn">
                  {saving ? "Emitiendo..." : "Emitir y firmar"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      <TemplatesManager
        open={tplMgrOpen}
        onOpenChange={setTplMgrOpen}
        templates={templates}
        currentUserId={user?.id}
        onChange={loadTemplates}
      />

      <Card>
        <CardHeader><CardTitle className="font-display">Informes emitidos</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {items.length === 0 && <div className="text-sm text-muted-foreground">Sin informes aún.</div>}
          {items.map((r) => (
            <div key={r.id} className="border border-border rounded-lg p-4 flex flex-wrap justify-between gap-3" data-testid={`mr-${r.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-teal-100 text-teal-700">Informe</Badge>
                  <span className="text-xs text-muted-foreground">{r.code} · {new Date(r.created_at).toLocaleString("es-ES")}</span>
                  {r.email_sent && <Badge variant="outline" className="text-xs">Enviado</Badge>}
                  {r.signature?.short && (
                    <span className="text-[10px] text-muted-foreground font-mono">Firma {r.signature.short}</span>
                  )}
                </div>
                <div className="font-medium mt-1 truncate">
                  {user?.role === "patient" ? `Dr. ${r.doctor_name}` : r.patient_name}
                </div>
                <div className="text-xs text-muted-foreground line-clamp-1">{r.reason}</div>
              </div>
              <div className="flex gap-2 items-start">
                <Button variant="outline" size="sm" onClick={() => generateMedicalReportPDF(r)} data-testid={`mr-pdf-${r.id}`}>
                  <Download className="w-4 h-4 mr-1" /> PDF
                </Button>
                {isDoctor && r.patient_email && !String(r.patient_email).endsWith("@nolinked.local") && (
                  <Button variant="outline" size="sm" onClick={() => resend(r)} data-testid={`mr-email-${r.id}`}>
                    <Mail className="w-4 h-4 mr-1" /> Email
                  </Button>
                )}
                {isDoctor && (
                  <Button variant="outline" size="icon" onClick={() => remove(r.id)} data-testid={`mr-delete-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
